import type { Expr, PathSegment } from "./ast.js";
import { parseExpression } from "./parser.js";
import { PIPES, compareValues, isEmptyValue, looseEquals, toDisplayString, toNumber, type FormatContext } from "./format.js";

/**
 * A lexical scope chain. Lookups walk from the innermost scope outwards, so a
 * repeater's `item` shadows a data source called `item` (and we warn about that
 * in validation).
 */
export class Scope {
  constructor(
    private readonly vars: Record<string, unknown>,
    private readonly parent?: Scope
  ) {}

  lookup(name: string): { found: boolean; value: unknown } {
    if (Object.prototype.hasOwnProperty.call(this.vars, name)) return { found: true, value: this.vars[name] };
    if (this.parent) return this.parent.lookup(name);
    return { found: false, value: undefined };
  }

  child(vars: Record<string, unknown>): Scope {
    return new Scope(vars, this);
  }

  /** Names visible in this scope (innermost first, deduplicated). */
  names(): string[] {
    const out: string[] = [];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let s: Scope | undefined = this;
    while (s) {
      for (const k of Object.keys(s.vars)) if (!out.includes(k)) out.push(k);
      s = s.parent;
    }
    return out;
  }
}

export interface EvalContext {
  scope: Scope;
  format: FormatContext;
  /** Optional sink for non-fatal problems encountered during evaluation. */
  onWarning?: (message: string, expr?: string) => void;
}

const FUNCTIONS: Record<string, (args: unknown[], ctx: EvalContext) => unknown> = {
  sum: ([list]) => numbers(list).reduce((a, b) => a + b, 0),
  count: ([list]) => (Array.isArray(list) ? list.length : isEmptyValue(list) ? 0 : 1),
  avg: ([list]) => {
    const ns = numbers(list);
    return ns.length ? ns.reduce((a, b) => a + b, 0) / ns.length : null;
  },
  min: ([list, ...rest]) => {
    const ns = rest.length ? numbers([list, ...rest]) : numbers(list);
    return ns.length ? Math.min(...ns) : null;
  },
  max: ([list, ...rest]) => {
    const ns = rest.length ? numbers([list, ...rest]) : numbers(list);
    return ns.length ? Math.max(...ns) : null;
  },
  first: ([list]) => (Array.isArray(list) ? list[0] : list),
  last: ([list]) => (Array.isArray(list) ? list[list.length - 1] : list),
  length: ([v]) => (Array.isArray(v) ? v.length : toDisplayString(v).length),
  coalesce: (args) => args.find((a) => !isEmptyValue(a)) ?? null,
  isEmpty: ([v]) => isEmptyValue(v),
  isNotEmpty: ([v]) => !isEmptyValue(v),
  contains: ([hay, needle]) =>
    Array.isArray(hay) ? hay.some((x) => looseEquals(x, needle)) : toDisplayString(hay).toLowerCase().includes(toDisplayString(needle).toLowerCase()),
  startsWith: ([s, p]) => toDisplayString(s).toLowerCase().startsWith(toDisplayString(p).toLowerCase()),
  endsWith: ([s, p]) => toDisplayString(s).toLowerCase().endsWith(toDisplayString(p).toLowerCase()),
  concat: (args) => args.map(toDisplayString).join(""),
  round: ([v, d]) => {
    const n = toNumber(v);
    if (n === null) return null;
    const m = 10 ** (toNumber(d) ?? 0);
    return Math.round(n * m) / m;
  },
  abs: ([v]) => {
    const n = toNumber(v);
    return n === null ? null : Math.abs(n);
  },
  number: ([v]) => toNumber(v),
  string: ([v]) => toDisplayString(v),
  if: ([c, a, b]) => (truthy(c) ? a : b),
  now: (_args, ctx) => ctx.format.now ?? new Date(),
  year: ([v]) => dateOf(v)?.getFullYear() ?? null,
  daysBetween: ([a, b]) => {
    const da = dateOf(a);
    const db = dateOf(b);
    return da && db ? Math.round((db.getTime() - da.getTime()) / 86_400_000) : null;
  }
};

export const FUNCTION_NAMES = Object.keys(FUNCTIONS);

function dateOf(v: unknown): Date | null {
  if (v instanceof Date) return v;
  if (typeof v === "string" || typeof v === "number") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function numbers(list: unknown): number[] {
  if (!Array.isArray(list)) {
    const n = toNumber(list);
    return n === null ? [] : [n];
  }
  const out: number[] = [];
  for (const v of list) {
    const n = toNumber(v);
    if (n !== null) out.push(n);
  }
  return out;
}

export function truthy(v: unknown): boolean {
  if (v === null || v === undefined || v === false || v === 0 || v === "") return false;
  if (typeof v === "string") return !["false", "no", "0", "off"].includes(v.trim().toLowerCase());
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * Safe property access: only own enumerable data properties, never prototype
 * members, never functions, never symbols.
 */
export function readProperty(obj: unknown, name: string): unknown {
  if (obj === null || obj === undefined) return undefined;
  if (typeof obj === "string" && name === "length") return obj.length;
  if (typeof obj !== "object") return undefined;
  if (Array.isArray(obj)) {
    if (name === "length") return obj.length;
    // Implicit projection: holdings.value → [h.value for h in holdings]
    const projected = obj.map((x) => readProperty(x, name));
    return projected.every((v) => v === undefined) ? undefined : projected;
  }
  if (!Object.prototype.hasOwnProperty.call(obj, name)) return undefined;
  const v = (obj as Record<string, unknown>)[name];
  return typeof v === "function" ? undefined : v;
}

export function resolvePath(segments: PathSegment[], scope: Scope): { found: boolean; value: unknown } {
  const first = segments[0];
  if (!first || first.kind !== "prop") return { found: false, value: undefined };
  const root = scope.lookup(first.name);
  if (!root.found) return { found: false, value: undefined };
  let cur = root.value;
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i]!;
    if (seg.kind === "prop") cur = readProperty(cur, seg.name);
    else cur = Array.isArray(cur) ? cur[seg.index] : undefined;
    if (cur === undefined) return { found: true, value: undefined };
  }
  return { found: true, value: cur };
}

export function evaluateAst(e: Expr, ctx: EvalContext): unknown {
  switch (e.kind) {
    case "literal":
      return e.value;
    case "path": {
      const r = resolvePath(e.segments, ctx.scope);
      return r.value;
    }
    case "unary": {
      const v = evaluateAst(e.arg, ctx);
      if (e.op === "not") return !truthy(v);
      const n = toNumber(v);
      return n === null ? null : -n;
    }
    case "binary":
      return evaluateBinary(e.op, e.left, e.right, ctx);
    case "conditional":
      return truthy(evaluateAst(e.test, ctx)) ? evaluateAst(e.then, ctx) : evaluateAst(e.else, ctx);
    case "call": {
      const fn = FUNCTIONS[e.name];
      if (!fn) {
        ctx.onWarning?.(`Unknown function "${e.name}"`);
        return null;
      }
      return fn(
        e.args.map((a) => evaluateAst(a, ctx)),
        ctx
      );
    }
    case "pipe": {
      const input = evaluateAst(e.input, ctx);
      const pipe = PIPES[e.name];
      if (!pipe) {
        ctx.onWarning?.(`Unknown formatter "${e.name}"`);
        return input;
      }
      return pipe.fn(
        input,
        e.args.map((a) => evaluateAst(a, ctx)),
        ctx.format
      );
    }
    case "array":
      return e.items.map((i) => evaluateAst(i, ctx));
  }
}

function evaluateBinary(op: string, leftE: Expr, rightE: Expr, ctx: EvalContext): unknown {
  if (op === "and") return truthy(evaluateAst(leftE, ctx)) ? truthy(evaluateAst(rightE, ctx)) : false;
  if (op === "or") return truthy(evaluateAst(leftE, ctx)) ? true : truthy(evaluateAst(rightE, ctx));
  const l = evaluateAst(leftE, ctx);
  const r = evaluateAst(rightE, ctx);
  switch (op) {
    case "==":
      return looseEquals(l, r);
    case "!=":
      return !looseEquals(l, r);
    case "<":
      return compareOrNull(l, r, (c) => c < 0);
    case "<=":
      return compareOrNull(l, r, (c) => c <= 0);
    case ">":
      return compareOrNull(l, r, (c) => c > 0);
    case ">=":
      return compareOrNull(l, r, (c) => c >= 0);
    case "in":
      return Array.isArray(r) ? r.some((x) => looseEquals(x, l)) : toDisplayString(r).includes(toDisplayString(l));
    case "+": {
      const nl = toNumber(l);
      const nr = toNumber(r);
      if (nl !== null && nr !== null && typeof l !== "string" && typeof r !== "string") return nl + nr;
      if (typeof l === "string" || typeof r === "string") return toDisplayString(l) + toDisplayString(r);
      return nl !== null && nr !== null ? nl + nr : null;
    }
    case "-":
    case "*":
    case "/":
    case "%": {
      const nl = toNumber(l);
      const nr = toNumber(r);
      if (nl === null || nr === null) return null;
      if (op === "-") return nl - nr;
      if (op === "*") return nl * nr;
      if (op === "/") return nr === 0 ? null : nl / nr;
      return nr === 0 ? null : nl % nr;
    }
  }
  return null;
}

function compareOrNull(l: unknown, r: unknown, test: (c: number) => boolean): boolean {
  if (l === null || l === undefined || r === null || r === undefined) return false;
  return test(compareValues(l, r));
}

/** Evaluates an expression string in a scope. Never throws for runtime data problems. */
export function evaluate(src: string, ctx: EvalContext): unknown {
  return evaluateAst(parseExpression(src), ctx);
}

/** Evaluates to a boolean using report truthiness rules. */
export function evaluateCondition(src: string, ctx: EvalContext): boolean {
  return truthy(evaluate(src, ctx));
}

/** Evaluates to display text. */
export function evaluateText(src: string, ctx: EvalContext): string {
  return toDisplayString(evaluate(src, ctx));
}

export interface TemplateSegment {
  kind: "text" | "token";
  text: string;
  /** Present for tokens: the expression source. */
  expr?: string;
}

/** Splits `Hello {{ client.firstName }}` into text/token segments. */
export function splitTemplate(template: string): TemplateSegment[] {
  const out: TemplateSegment[] = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template))) {
    if (m.index > last) out.push({ kind: "text", text: template.slice(last, m.index) });
    out.push({ kind: "token", text: m[0], expr: m[1]! });
    last = m.index + m[0].length;
  }
  if (last < template.length) out.push({ kind: "text", text: template.slice(last) });
  return out;
}

/** Interpolates `{{ expr }}` tokens in a string. */
export function interpolate(template: string, ctx: EvalContext): string {
  if (!template.includes("{{")) return template;
  return splitTemplate(template)
    .map((s) => {
      if (s.kind === "text") return s.text;
      try {
        return evaluateText(s.expr!, ctx);
      } catch {
        ctx.onWarning?.(`Invalid expression "${s.expr}"`, s.expr);
        return "";
      }
    })
    .join("");
}
